<script lang="ts" setup>
import { debounce } from "lodash-es";
import type * as Monaco from "monaco-editor";
import type { CancellationToken, editor, IDisposable } from "monaco-editor";
import { useVModel } from "@vueuse/core";
import type { FieldDefinition } from "~/models/Automation";

const props = withDefaults(
    defineProps<{
        mjml?: boolean;
        onMonacoMounted?: (monaco: typeof Monaco) => void;
        onNonExistingFieldMatch?: (
            monaco: typeof Monaco,
            context: {
                model: editor.ITextModel;
                position: Monaco.Position;
                token: CancellationToken;
            },
            match: RegExpExecArray,
        ) => Monaco.languages.ProviderResult<Monaco.languages.Hover>;
        code: string;
        lang?:
            | "mjml"
            | "json"
            | "javascript"
            | "typescript"
            | "html"
            | "xml"
            | "graphql"
            | "plaintext";
        fields?: FieldDefinition[];
        delay?: number;
        autoHeight?: boolean;
        minHeight?: number;
        maxHeight?: number;
    }>(),
    {
        delay: 250,
        fields: () => [],
        autoHeight: false,
        minHeight: 160,
        maxHeight: 480,
    },
);

const emit = defineEmits(["update:code"]);

const code = useVModel(props, "code", debounce(emit, props.delay));
const editorRef = shallowRef<Monaco.editor.IStandaloneCodeEditor>();

const MJML = [
    "mj-accordion",
    "mj-accordion-element",
    "mj-accordion-title",
    "mj-accordion-text",
    "mj-button",
    "mj-carousel",
    "mj-column",
    "mj-divider",
    "mj-group",
    "mj-hero",
    "mj-image",
    "mj-navbar",
    "mj-navbar-link",
    "mj-raw",
    "mj-section",
    "mj-social",
    "mj-social-element",
    "mj-spacer",
    "mj-table",
    "mj-text",
    "mj-wrapper",
    "mj-attributes",
    "mj-class",
    "mj-all",
    "mj-breakpoint",
    "mj-font",
    "mj-html-attributes",
    "mj-selector",
    "mj-html-attribute",
    "mj-preview",
    "mj-style",
    "mj-title",
    "mjml",
    "mj-head",
    "mj-body",
    "mj-include",
];

function getSuggestionItems(
    monaco: typeof Monaco,
): Monaco.languages.CompletionItem[] {
    return props.fields.map(
        (field) =>
            ({
                label: {
                    label: field.key,
                    detail: ` ${field.sub_type ? `${field.type}<${field.sub_type}>` : field.type}`,
                    description: field.label,
                },
                detail: `${field.label ?? field.key}`,
                documentation: field.metadata?.documentation
                    ? {
                          value: field.metadata?.documentation,
                          supportHtml: true,
                          isTrusted: true,
                      }
                    : undefined,
                kind: monaco.languages.CompletionItemKind.Text,
                insertText: field.key,
            }) as Monaco.languages.CompletionItem,
    );
}

const providers = ref<IDisposable[]>();
const contentHeight = ref<number>(props.minHeight);

function onMonacoMounted(
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco,
) {
    const disposables: Monaco.IDisposable[] = [];
    editorRef.value = editor;

    if (props.autoHeight) {
        const applyHeight = () => {
            contentHeight.value = Math.min(
                props.maxHeight,
                Math.max(props.minHeight, editor.getContentHeight()),
            );
        };

        disposables.push(editor.onDidContentSizeChange(applyHeight));
        applyHeight();
    }
    if (props.mjml) {
        disposables.push(
            monaco.languages.registerCompletionItemProvider("html", {
                triggerCharacters: ["<", "</"],
                provideCompletionItems: (model, position, context) => {
                    if (model !== editorRef.value?.getModel()) {
                        return { suggestions: [] };
                    }
                    if (!context.triggerCharacter) return { suggestions: [] };
                    return {
                        suggestions: MJML.map((key) => ({
                            label: key,
                            insertText: key,
                            kind: monaco.languages.CompletionItemKind.Property,
                        })) as Monaco.languages.CompletionItem[],
                    };
                },
            }),
        );
    }

    disposables.push(
        monaco.languages.registerDocumentSymbolProvider("html", {
            provideDocumentSymbols(
                model: editor.ITextModel,
                token: CancellationToken,
            ): Monaco.languages.ProviderResult<
                Monaco.languages.DocumentSymbol[]
            > {
                return [];
            },
        }),
    );

    const language = props.lang ?? "html";

    disposables.push(
        monaco.languages.registerHoverProvider(language, {
            provideHover(
                model: editor.ITextModel,
                position: Monaco.Position,
                token: CancellationToken,
            ): Monaco.languages.ProviderResult<Monaco.languages.Hover> {
                if (model !== editorRef.value?.getModel()) {
                    return { contents: [] };
                }

                const line = model.getLineContent(position.lineNumber);

                const matches = FieldCollection.match(line, "{{", "}}").filter(
                    (match) => {
                        return (
                            position.column >= match.index &&
                            position.column < match.index + match[0].length
                        );
                    },
                );
                const match = matches[0];

                if (match) {
                    const field = props.fields.find(
                        (field) => field.key === match[1],
                    );

                    if (field) {
                        return {
                            contents: [
                                {
                                    value:
                                        (field.metadata
                                            ?.documentation as string) ??
                                        field.label,
                                    supportHtml: true,
                                    isTrusted: true,
                                },
                            ],
                            range: new monaco.Range(
                                position.lineNumber,
                                match.index + 1,
                                position.lineNumber,
                                match.index + match[0].length + 1,
                            ),
                        };
                    } else if (props.onNonExistingFieldMatch) {
                        return props.onNonExistingFieldMatch(
                            monaco,
                            {
                                model,
                                position,
                                token,
                            },
                            match,
                        );
                    }
                }

                return {
                    contents: [],
                };
            },
        }),
    );

    disposables.push(
        monaco.languages.registerCompletionItemProvider(language, {
            triggerCharacters: ["{{"],
            provideCompletionItems: (model, position, context) => {
                if (model !== editorRef.value?.getModel()) {
                    return { suggestions: [] };
                }

                return {
                    suggestions: getSuggestionItems(monaco),
                };
            },
        }),
    );

    providers.value = disposables;
    if (props.onMonacoMounted) props.onMonacoMounted(monaco);
}

onBeforeUnmount(() => {
    providers.value?.forEach((disposable) => disposable.dispose());
    editorRef.value?.dispose();
});
</script>

<template>
    <div
        class="w-full"
        :class="autoHeight ? undefined : 'h-full'"
        :style="autoHeight ? { height: `${contentHeight}px` } : undefined"
    >
        <vue-monaco-editor
            v-model:value="code"
            :options="{
                automaticLayout: true,
                formatOnType: false,
                formatOnPaste: false,
                ...(autoHeight ? { scrollBeyondLastLine: false } : {}),
            }"
            :default-language="props.lang ?? 'html'"
            theme="vs-dark"
            @mount="onMonacoMounted"
        />
    </div>
</template>

<style scoped></style>
